<template>
  <vs-wrapper>
    <v-progress-circular v-if="isLoading" :size="64" :width="6" indeterminate color="primary" />

    <template v-else>
      <AssetCard
        v-for="(asset, id) in sortedInventoryAssets"
        :key="`${locationInventory.id}-${id}`"
        :asset="asset"
        :can-edit-inventory="canEditInventory"
        @edit-inventory="asset.id && $emit('edit-inventory', asset.id)"
      />
      <vs-text v-if="!hasInventory" type="subdued"> No available inventory here </vs-text>
      <v-menu
        v-if="canEditInventory"
        v-model="showMenu"
        max-height="80vh"
        :close-on-content-click="false"
        :nudge-bottom="10"
        :nudge-width="80"
        class="rounded-xl"
        offset-y
        rounded="xl"
      >
        <template #activator="{ on }">
          <vs-button small label="Add inventory" type="secondary" v-on="on" />
        </template>
        <v-card>
          <v-list v-if="showMenu">
            <v-subheader class="list-subheader">Select Type</v-subheader>
            <v-list-item
              v-for="inventoryType in sortedInventoryTypes"
              :key="inventoryType.id"
              @click="inventoryType.id && editInventory(inventoryType.id)"
            >
              <v-list-item-title>{{ inventoryType.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </template>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import AssetCard from '../inventory/AssetCard.vue'
import LocationDTO from '@/models/location'
import { Asset } from '@/models/asset'
import LocationInventory from '@/models/location_inventory'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'

interface Props {
  location: LocationDTO
  canEditInventory?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canEditInventory: false,
})

const emits = defineEmits<{
  (e: 'edit-inventory', assetId: number): void
}>()

const store = useStore()
const showMenu = ref(false)
const { allInventoryTypes } = useInventoryTypes()

// Computed properties using store getters
const isLoading = computed<boolean>(() => store.getters['inventory/isLoading'])
const getInventoryById = computed<(id: number) => LocationInventory>(
  () => store.getters['inventory/getById'],
)

const sortedInventoryTypes = computed(() => {
  return allInventoryTypes.value?.sort((a, b) => a.name.localeCompare(b.name)) || []
})

const locationInventory = computed(() => {
  return (
    (props.location.id && getInventoryById.value(props.location.id)) || {
      assets: {},
      id: -1,
    }
  )
})

const sortedInventoryAssets = computed(() => {
  return (Object.values(locationInventory.value.assets || {}) as Asset[]).sort(
    (a: Asset, b: Asset) => (a.name || '').localeCompare(b.name || ''),
  )
})

const hasInventory = computed(() => {
  return (
    locationInventory.value &&
    locationInventory.value.assets &&
    Object.keys(locationInventory.value.assets).length > 0
  )
})

// Methods
const editInventory = (assetId: number) => {
  showMenu.value = false
  emits('edit-inventory', assetId)
}

// Lifecycle
onMounted(() => {
  store.dispatch('inventory/fetchLocationInventory', props.location)
})
</script>
<style scoped>
.list-subheader {
  font-weight: bold;
  color: black;
  font-family: Montserrat, serif;
}
</style>
